import type { TablerIcon } from '@tabler/icons-react';

import { decideAccess, type Viewer } from './access';
import { type NavGroup, type RouteDefinition, routeTable } from './route-table';

/**
 * The navigation a viewer may be shown, derived from the route table.
 *
 * Not a hand-kept list of links. A second list is a second place to forget, and
 * the thing it forgets is the one that matters: a link to a screen the guard
 * refuses, shown to somebody who then cannot open it. Filtering the same table
 * through the same `decideAccess` the guard uses makes that particular
 * disagreement impossible — a link appears exactly when the route would render.
 *
 * **This is courtesy, not security.** The role it filters on comes from the
 * session in `sessionStorage`, which is editable by whoever is at the keyboard;
 * anybody can put a link back by typing the URL, and the guard will still refuse
 * it, and the server will still refuse it after that. What hiding buys is that
 * staff are not shown controls that answer them with a 403 they cannot act on —
 * `docs/ui-conventions.md` asks for exactly that, and for the same reason.
 */
export interface NavItem {
  readonly path: string;
  readonly label: string;
  /** The section it sits under, or absent for an item that stands alone. */
  readonly group?: NavGroup;
  readonly icon: TablerIcon;
}

/**
 * A run of navigation items under one heading, or under none.
 *
 * `heading` is `null` for the run that opens the list — Overview — which has no
 * section because it is not one of the park's areas of work; it is the way back
 * to all of them.
 */
export interface NavSection {
  readonly heading: NavGroup | null;
  readonly items: readonly NavItem[];
}

/**
 * Narrowing rather than a `?.` at the point of use: `filter` does not tell the
 * compiler that `nav` survived, and `nav?.icon ?? something` would have to
 * invent an icon for a route that cannot reach this line.
 */
interface NavRoute extends RouteDefinition {
  readonly nav: NonNullable<RouteDefinition['nav']>;
}

function isNavRoute(route: RouteDefinition): route is NavRoute {
  return route.nav !== undefined;
}

export function navigationFor(
  viewer: Viewer | null,
  routes: readonly RouteDefinition[] = routeTable,
): NavItem[] {
  return routes
    .filter(isNavRoute)
    .filter((route) => decideAccess(route.access, viewer) === 'render')
    .sort((left, right) => left.nav.order - right.nav.order)
    .map((route) => ({
      path: route.path,
      label: route.nav.label,
      ...(route.nav.group === undefined ? {} : { group: route.nav.group }),
      icon: route.nav.icon,
    }));
}

/**
 * The same items, cut into the sections the margin draws.
 *
 * Cut on **contiguous runs of `group`**, not by collecting every item that
 * names a section. That is deliberate: `nav.order` stays the single statement
 * of sequence, and a section is simply where that sequence changes subject. An
 * item filed into a group but ordered away from it would show up here as its
 * own heading rather than being quietly teleported — visible, and fixable in
 * the one place the order is written.
 *
 * Role filtering has already happened by the time this runs, so a section whose
 * every item was filtered out never appears. A heading with nothing under it is
 * the one thing that would tell somebody about a screen they may not open.
 */
export function navigationSections(items: readonly NavItem[]): NavSection[] {
  const sections: NavSection[] = [];

  for (const item of items) {
    const last = sections.at(-1);

    if (last !== undefined && last.heading === (item.group ?? null)) {
      sections[sections.length - 1] = { heading: last.heading, items: [...last.items, item] };
      continue;
    }

    sections.push({ heading: item.group ?? null, items: [item] });
  }

  return sections;
}
